/**
 * Google reCAPTCHA Enterprise v3 utility
 * 
 * This module provides functions to load and execute reCAPTCHA Enterprise v3
 * for bot protection on login and signup forms.
 */

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

let recaptchaLoaded = false;
let loadPromise = null;

/**
 * Show the reCAPTCHA badge (call on mount of login/signup pages)
 */
export const showRecaptchaBadge = () => {
  document.body.classList.add('show-recaptcha');
};

/**
 * Hide the reCAPTCHA badge (call on unmount of login/signup pages)
 */
export const hideRecaptchaBadge = () => {
  document.body.classList.remove('show-recaptcha');
};

/**
 * Load the reCAPTCHA Enterprise script dynamically
 * @returns {Promise<void>}
 */
export const loadRecaptcha = () => {
  if (recaptchaLoaded) {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  if (!RECAPTCHA_SITE_KEY) {
    console.warn('reCAPTCHA site key not configured. Skipping reCAPTCHA.');
    return Promise.resolve();
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      recaptchaLoaded = true;
      resolve();
    };

    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load reCAPTCHA script'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
};

/**
 * Execute reCAPTCHA and get a token for the specified action
 * @param {string} action - The action name (e.g., 'LOGIN', 'SIGNUP')
 * @returns {Promise<string|null>} - The reCAPTCHA token or null if not available
 */
export const executeRecaptcha = async (action) => {
  if (!RECAPTCHA_SITE_KEY) {
    console.warn('reCAPTCHA site key not configured. Returning null token.');
    return null;
  }

  try {
    await loadRecaptcha();

    // Wait for grecaptcha.enterprise to be ready
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha || !window.grecaptcha.enterprise) {
        reject(new Error('reCAPTCHA not loaded'));
        return;
      }

      window.grecaptcha.enterprise.ready(async () => {
        try {
          const token = await window.grecaptcha.enterprise.execute(
            RECAPTCHA_SITE_KEY,
            { action }
          );
          resolve(token);
        } catch (error) {
          console.error('reCAPTCHA execution failed:', error);
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('Failed to execute reCAPTCHA:', error);
    return null;
  }
};

/**
 * Check if reCAPTCHA is enabled (site key is configured)
 * @returns {boolean}
 */
export const isRecaptchaEnabled = () => {
  return !!RECAPTCHA_SITE_KEY;
};

export default {
  loadRecaptcha,
  executeRecaptcha,
  isRecaptchaEnabled,
};
